import React, { useState, useEffect } from "react";
import "../../Styles/UpdateFaculty.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Loader from "../../Components/Loader";

function UpdateFaculty() {
  const [activeTab, setActiveTab] = useState("pending");
  const [showModal, setShowModal] = useState(false);
  const [selectedFaculty, setSelectedFaculty] = useState(null);
  const [pendingData, setPendingData] = useState([]);
  const [completedData, setCompletedData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    if (activeTab === "pending") {
      fetchPendingData();
    } else {
      fetchCompletedData();
    }
  }, [activeTab]);

  const fetchPendingData = async () => {
    const authToken = localStorage.getItem("authToken");
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        "https://api.uniums.com/api/admin/faculty/pending",
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch pending faculty data.");
      }

      const result = await response.json();
      setPendingData(
        result.data.map((faculty) => ({ ...faculty, status: "pending" }))
      );
    } catch (err) {
      setError(err.message);
      toast.error(err.message || "Error fetching pending data.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    const fetchPendingCount = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(
          "https://api.uniums.com/api/admin/faculty/pending/count",
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch pending faculty count.");
        }

        const result = await response.json();
        setPendingCount(result.count);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPendingCount();
  }, []);

  const fetchCompletedData = async () => {
    const authToken = localStorage.getItem("authToken");
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        "https://api.uniums.com/api/admin/faculty/completed",
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch completed faculty data.");
      }

      const result = await response.json();
      setCompletedData(
        result.data.map((faculty) => ({ ...faculty, status: "completed" }))
      );
    } catch (err) {
      setError(err.message);
      toast.error(err.message || "Error fetching completed data.");
    } finally {
      setLoading(false);
    }
  };

  const fetchFacultyDetails = async (email) => {
    try {
      const response = await fetch(
        `https://api.uniums.com/api/admin/faculty/details?email=${email}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch faculty details.");
      }

      const result = await response.json();
      return result.data;
    } catch (err) {
      toast.error(err.message || "Error fetching faculty details.");
      return null;
    }
  };




  const fetchAdminData = async () => {
    const authToken = localStorage.getItem("authToken");
    if (!authToken) {
      setError("No authentication token found.");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(
        "https://api.uniums.com/api/admin/admin/data",
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch admin data.");
      }

      const result = await response.json();

      if (result && result.classes && result.subjects && result.semesters) {
        return result;
      } else {
        throw new Error("No valid admin data found.");
      }
    } catch (err) {
      setError(err.message);
      toast.error(err.message || "Error fetching admin data.");
      return null;
    }
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  const handleAddDetailsClick = async (faculty) => {
    setLoading(true);
    try {
      const adminData = await fetchAdminData();
      if (adminData) {
        let existingDetails = null;
        if (faculty.status === "completed") {
          // Fetch faculty details for completed faculty
          const facultyDetails = await fetchFacultyDetails(faculty.email);
          
          if (facultyDetails) {
            existingDetails = {
              selectedClasses: facultyDetails.selectedClasses || [],
              selectedSubjects: facultyDetails.selectedSubjects || [],
              address: facultyDetails.address || '',
              pinCode: facultyDetails.pinCode || '',
              state: facultyDetails.state || ''
            };
          }
        }

        setSelectedFaculty({
          ...faculty,
          adminData: { ...adminData },
          selectedClasses: existingDetails?.selectedClasses || [],
          selectedSubjects: existingDetails?.selectedSubjects || [],
          address: existingDetails?.address || '',
          pinCode: existingDetails?.pinCode || '',
          state: existingDetails?.state || '',
        });
      }
      setShowModal(true);
    } catch (error) {
      toast.error(error.message || "Error fetching admin data.");
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxChange = (e, type) => {
    const { name, checked } = e.target;
    setSelectedFaculty((prevState) => {
      const updatedSelection = { ...prevState };
      if (type === "classes") {
        if (checked) {
          updatedSelection.selectedClasses.push(name);
        } else {
          updatedSelection.selectedClasses =
            updatedSelection.selectedClasses.filter((item) => item !== name);
        }
      }
      if (type === "subjects") {
        if (checked) {
          updatedSelection.selectedSubjects.push(name);
        } else {
          updatedSelection.selectedSubjects =
            updatedSelection.selectedSubjects.filter((item) => item !== name);
        }
      }
      return updatedSelection;
    });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedFaculty(null);
  };

  const dataToShow = activeTab === "pending" ? pendingData : completedData;

  const handleSaveChanges = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        "https://api.uniums.com/api/admin/faculty/save",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
          body: JSON.stringify({
            name: selectedFaculty.name,
            email: selectedFaculty.email,
            employeeCode: selectedFaculty.employeeCode,
            address: selectedFaculty.address,
            pinCode: selectedFaculty.pinCode,
            state: selectedFaculty.state,
            selectedClasses: selectedFaculty.selectedClasses,
            selectedSubjects: selectedFaculty.selectedSubjects,
          }),
        }
      );
      
      if (!response.ok) {
        throw new Error("Failed to save faculty details");
      }

      const result = await response.json();
      toast.success(result.message || "Faculty details saved successfully.");
      
      // Update the data based on the current active tab
      if (activeTab === "pending") {
        setPendingData((prevData) =>
          prevData.map((faculty) =>
            faculty.email === selectedFaculty.email
              ? { ...faculty, status: "completed" }
              : faculty
          )
        );
      } else {
        // Refresh completed data if in completed tab
        fetchCompletedData();
      }
      
      handleCloseModal();
    } catch (error) {
      toast.error(error.message || "Error saving faculty details.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="update-faculty-container">
      <h1>Update Faculties</h1>

      <div className="tabs">
        <button
          className={activeTab === "pending" ? "active-tab" : ""}
          onClick={() => handleTabClick("pending")}
        >
          Pending
        </button>
        <p className="count_pending">
          <strong>{pendingCount}</strong>
        </p>
        <button
          className={activeTab === "completed" ? "active-tab" : ""}
          onClick={() => handleTabClick("completed")}
        >
          Completed
        </button>
      </div>
      {loading && <Loader />}
      {loading && <p className="p_load">Loading...</p>}
      {error && <p className="error">{error}</p>}

      {!loading && !error && (
        <div className="admin-table-container">
          <table className="faculty-table">
            <thead>
              <tr>
                <th>SL</th>
                <th>Name</th>
                <th>Email</th>
                <th>User Code</th>
                <th>Employee Code</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {dataToShow.length > 0 ? (
                dataToShow.map((faculty, index) => (
                  <tr key={faculty._id}>
                    <td>{index + 1}</td>
                    <td>{faculty.name}</td>
                    <td>{faculty.email}</td>
                    <td>{faculty.role}</td>
                    <td>{faculty.employeeCode}</td>
                    <td>
                      {faculty.status === "completed" ? (
                        <button
                          className="action_button completed_button"
                          onClick={() => handleAddDetailsClick(faculty)}
                        >
                          Completed
                        </button>
                      ) : (
                        <button
                          className="action_button"
                          onClick={() => handleAddDetailsClick(faculty)}
                        >
                          Add Details
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3">No data available</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal_overlay1">
          <div className="modal_new">
            <div className="modal-header">
              <h2>Add Details {selectedFaculty.name}</h2>
              <button
                className="close_button close_button_1"
                onClick={handleCloseModal}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              {selectedFaculty && (
                <form>
                  {/* Single-line input fields */}
                  <div className="form_group row_layout">
                    <div className="field">
                      <label>Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={selectedFaculty.name}
                        readOnly
                        disabled
                      />
                    </div>
                    <div className="field">
                      <label>Email</label>
                      <input
                        type="text"
                        className="form-control"
                        value={selectedFaculty.email}
                        readOnly
                        disabled
                      />
                    </div>
                    <div className="field">
                      <label>Employee Code</label>
                      <input
                        type="text"
                        className="form-control"
                        value={selectedFaculty.employeeCode}
                        readOnly
                        disabled
                      />
                    </div>
                  </div>

                  {/* Checkbox Lists */}
                  {selectedFaculty.adminData && (
                    <>
                      <div className="form_group">
                        <label>Classes</label>
                        <div className="checkbox_group">
                          {selectedFaculty.adminData.classes.map((cls, idx) => (
                            <label key={idx} className="checkbox_item">
                              <input
                                type="checkbox"
                                name={cls}
                                checked={selectedFaculty.selectedClasses.includes(
                                  cls
                                )}
                                onChange={(e) =>
                                  handleCheckboxChange(e, "classes")
                                }
                              />
                              {cls}
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="form_group">
                        <label>Subjects</label>
                        <div className="checkbox_group">
                          {selectedFaculty.adminData.subjects.map(
                            (subject, idx) => (
                              <label key={idx} className="checkbox_item">
                                <input
                                  type="checkbox"
                                  name={subject}
                                  checked={selectedFaculty.selectedSubjects.includes(
                                    subject
                                  )}
                                  onChange={(e) =>
                                    handleCheckboxChange(e, "subjects")
                                  }
                                />
                                {subject}
                              </label>
                            )
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Additional Inputs */}
                  <div className="form_group">
                    <label>Address</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter address"
                      value={selectedFaculty.address || ''}
                      onChange={(e) =>
                        setSelectedFaculty({
                          ...selectedFaculty,
                          address: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="form_group">
                    <label>Pin Code</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter pin code"
                      value={selectedFaculty.pinCode || ''}
                      onChange={(e) =>
                        setSelectedFaculty({
                          ...selectedFaculty,
                          pinCode: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="form_group">
                    <label>State</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter state"
                      value={selectedFaculty.state || ''}
                      onChange={(e) =>
                        setSelectedFaculty({
                          ...selectedFaculty,
                          state: e.target.value,
                        })
                      }
                    />
                  </div>
                </form>
              )}
            </div>
            <div className="modal-footer">
              <button className="save_button" onClick={handleSaveChanges}>
                Save Changes
              </button>
              <button className="close_button" onClick={handleCloseModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UpdateFaculty;
